/**
 * Settings store: persisted to localStorage (`tonist.settings`); when signed in, merged from and
 * debounced-PUT to /api/me/settings.
 */
import { create } from 'zustand';
import { SCALES, SettingsSchema, type Settings } from '@tonist/shared';
import { get as apiGet, put as apiPut } from '../lib/api';
import { stepTonicFifths, stepTonicSemitones } from '../music/tuning';

export const SETTINGS_KEY = 'tonist.settings';
export const DEFAULT_SETTINGS: Settings = SettingsSchema.parse({});

export function loadLocalSettings(storage: Pick<Storage, 'getItem'> | null = safeStorage()): Settings {
  try {
    const raw = storage?.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return SettingsSchema.parse({ ...DEFAULT_SETTINGS, ...JSON.parse(raw) });
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function saveLocalSettings(s: Settings, storage: Pick<Storage, 'setItem'> | null = safeStorage()): void {
  try { storage?.setItem(SETTINGS_KEY, JSON.stringify(s)); } catch { /* quota / private mode */ }
}

export function safeStorage(): Storage | null {
  try { return typeof localStorage !== 'undefined' ? localStorage : null; } catch { return null; }
}

/** Merge server settings over local (server wins for keys it has). */
export function mergeSettings(local: Settings, server: unknown): Settings {
  if (!server || typeof server !== 'object') return local;
  const parsed = SettingsSchema.safeParse({ ...local, ...(server as object) });
  return parsed.success ? parsed.data : local;
}

export interface SettingsState extends Settings {
  /** show the four extra scales in the tune sheet */
  showExtraScales: boolean;
  set(partial: Partial<Settings>): void;
  setTonic(pc: number): void;
  stepTonic(dir: number, bySemitone?: boolean): void;
  setScale(id: string): void;
  stepScale(dir: number): void;
  selectScaleIndex(i: number): void;
  toggleMode(): void;
  setDroneIds(ids: string[]): void;
  setDroneSlot(slot: number, id: string): void;
  nudgeBrightness(delta: number): void;
  nudgeWidth(delta: number): void;
  toggleExtraScales(): void;
  /** live macro nudges, not persisted */
  brightnessNudge: number;
  widthNudge: number;
  reset(): void;
}

const pick = (s: SettingsState): Settings => SettingsSchema.parse({
  tonicPc: s.tonicPc, scaleId: s.scaleId, ji: s.ji, mode: s.mode, assignment: s.assignment, droneIds: s.droneIds,
  calm: s.calm, theme: s.theme, volume: s.volume, debugAudio: s.debugAudio, pitchEngine: s.pitchEngine,
});

export const useSettings = create<SettingsState>((set, getState) => ({
  ...loadLocalSettings(),
  showExtraScales: false,
  brightnessNudge: 0,
  widthNudge: 0,
  set: (partial) => set(partial),
  setTonic: (pc) => set({ tonicPc: ((pc % 12) + 12) % 12 }),
  stepTonic: (dir, bySemitone = false) => set((s) => ({ tonicPc: bySemitone ? stepTonicSemitones(s.tonicPc, dir) : stepTonicFifths(s.tonicPc, dir) })),
  setScale: (id) => { if (SCALES.some((sc) => sc.id === id)) set({ scaleId: id }); },
  stepScale: (dir) => set((s) => {
    const list = s.showExtraScales ? SCALES : SCALES.filter((sc) => !sc.extra);
    const i = Math.max(0, list.findIndex((sc) => sc.id === s.scaleId));
    const next = list[(((i + Math.sign(dir)) % list.length) + list.length) % list.length]!;
    return { scaleId: next.id };
  }),
  selectScaleIndex: (i) => { const sc = SCALES[i]; if (sc) set({ scaleId: sc.id, showExtraScales: getState().showExtraScales || !!sc.extra }); },
  toggleMode: () => set((s) => ({ mode: s.mode === 'play' ? 'continuous' : 'play' })),
  setDroneIds: (ids) => set({ droneIds: ids.length ? ids : ['sa'] }),
  setDroneSlot: (slot, id) => set((s) => { const ids = [...s.droneIds]; while (ids.length <= slot) ids.push(ids[0] ?? 'sa'); ids[slot] = id; return { droneIds: ids }; }),
  nudgeBrightness: (d) => set((s) => ({ brightnessNudge: Math.max(-1, Math.min(1, s.brightnessNudge + d)) })),
  nudgeWidth: (d) => set((s) => ({ widthNudge: Math.max(-1, Math.min(1, s.widthNudge + d)) })),
  toggleExtraScales: () => set((s) => ({ showExtraScales: !s.showExtraScales })),
  reset: () => set({ ...DEFAULT_SETTINGS }),
}));

/** Current persisted subset. */
export const settingsSnapshot = (): Settings => pick(useSettings.getState());

// ---- persistence: localStorage always ----
let lastSaved = JSON.stringify(settingsSnapshot());
useSettings.subscribe((s) => {
  const snap = pick(s);
  const json = JSON.stringify(snap);
  if (json === lastSaved) return;
  lastSaved = json;
  saveLocalSettings(snap);
  if (serverSync) serverSync.schedule(snap);
});

// ---- server sync when signed in ----
interface ServerSync { schedule(s: Settings): void; stop(): void }
let serverSync: ServerSync | null = null;

export function debounced<T>(fn: (v: T) => void, ms: number, timers: { setTimeout: typeof setTimeout; clearTimeout: typeof clearTimeout } = globalThis) {
  let t: ReturnType<typeof setTimeout> | null = null;
  let pending: T | undefined;
  const flush = () => { if (t) { timers.clearTimeout(t); t = null; } if (pending !== undefined) { const v = pending; pending = undefined; fn(v); } };
  return {
    call(v: T) { pending = v; if (t) timers.clearTimeout(t); t = timers.setTimeout(flush, ms); },
    flush,
    cancel() { if (t) timers.clearTimeout(t); t = null; pending = undefined; },
  };
}

/** Begin syncing with /api/me/settings (call when a user signs in). Returns a stop function. */
export async function startSettingsServerSync(): Promise<() => void> {
  stopSettingsServerSync();
  const putter = debounced<Settings>((s) => { void apiPut('/me/settings', s).catch(() => { /* offline: local copy still wins */ }); }, 800);
  serverSync = { schedule: (s) => putter.call(s), stop: () => { putter.flush(); } };
  try {
    const server = await apiGet<unknown>('/me/settings');
    const merged = mergeSettings(settingsSnapshot(), (server as { settings?: unknown })?.settings ?? server);
    lastSaved = JSON.stringify(merged);
    saveLocalSettings(merged);
    useSettings.setState(merged);
  } catch {
    // no server copy yet: push ours
    putter.call(settingsSnapshot());
  }
  return stopSettingsServerSync;
}

export function stopSettingsServerSync(): void {
  serverSync?.stop();
  serverSync = null;
}

/** Apply theme to the document. */
export function applyTheme(theme: Settings['theme']): void {
  if (typeof document === 'undefined') return;
  const resolved = theme === 'system'
    ? (typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark')
    : theme;
  document.documentElement.dataset.theme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', resolved === 'light' ? '#F3EEE3' : '#0B0A0F');
}

export const resolvedTheme = (theme: Settings['theme']): 'dark' | 'light' =>
  theme === 'system' ? (typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark') : theme;

export const prefersReducedMotion = (): boolean => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
